import {
	faArrowRight as fasArrowRight,
	faHouse as fasHouse,
	faMaximize as fasMaximize,
	faSuitcaseMedical as fasSuitcaseMedical,
} from "@awesome.me/kit-3777c2145e/icons/classic/solid";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import autobind from "autobind-decorator";
import type { FormApi } from "final-form";
import type { ReactNode } from "react";
import { Card } from "react-bootstrap";
import type { AbstractSubmitStepProps } from "@/component/assets/AbstractLeadForm/Steps/AbstractSubmitStep";
import { AbstractSubmitStep } from "@/component/assets/AbstractLeadForm/Steps/AbstractSubmitStep";
import { CONFIG } from "@/component/Mortgage/Calculator/data";
import type { CommonOptions } from "@/shared/ui";
import { FormCurrency, FormDecidable, FormSelect, ProgressButton } from "@/shared/ui";
import { formatText } from "@/shared/ui/format";

export interface StepFields {
	realEstateValue: number;
	realEstateSizeId: number;
	ownRealEstateEnlargement: boolean;
	buyFromCloseRelative: boolean;
	earlierBoughtRealEstate: boolean;
	realEstateBuying: number;
}

interface Props extends AbstractSubmitStepProps {
	realEstateSizes: CommonOptions<number>;
	realEstateBuyings: CommonOptions<number>;
	loanGoal: string;
}

export class Step2 extends AbstractSubmitStep<StepFields, Props> {
	protected initValues(): StepFields {
		return {
			realEstateValue: undefined,
			realEstateSizeId: undefined,
			ownRealEstateEnlargement: undefined,
			buyFromCloseRelative: undefined,
			earlierBoughtRealEstate: undefined,
			realEstateBuying: undefined,
		};
	}

	protected renderContent(_values: StepFields, form: FormApi<StepFields>): ReactNode {
		const { realEstateSizes, loanGoal, realEstateBuyings, submitState } = this.props;
		const disabled = this.isDisabled();

		let realEstateBuyingsLabel = "Kiválasztottad már a lakást/házat?";

		if (loanGoal === "EPITES") {
			realEstateBuyingsLabel = "Mikor szeretnéd elkezdeni az építkezést?";
		} else if (loanGoal === "BOVITES") {
			realEstateBuyingsLabel = "Mikor szeretnéd elkezdeni a bővítést?";
		}

		return (
			<>
				<Card.Body>
					<FormCurrency
						label="Mennyi az ingatlan értéke?"
						name="realEstateValue"
						placeholder="Ingatlan érték"
						prepend={<FontAwesomeIcon icon={fasHouse} />}
						validate={this.validRealEstateValue}
						disabled={disabled}
						required
					/>
					<FormSelect
						label="Mekkora az ingatlan alapterülete?"
						name="realEstateSizeId"
						placeholder="Kérjük, válassz!"
						prepend={<FontAwesomeIcon icon={fasMaximize} />}
						options={realEstateSizes}
						disabled={disabled}
						required
					/>
					{loanGoal === "BOVITES" && (
						<FormDecidable
							label="Saját ingatlanodat bővíted a CSOK-kal?"
							name="ownRealEstateEnlargement"
							disabled={disabled}
							required
						/>
					)}
					{["UJ_LAKAS", "HASZNALT_LAKAS"].includes(loanGoal) && (
						<>
							<FormDecidable
								label="Közeli hozzátartozódtól, élettársadtól vásárolsz ingatlant?"
								name="buyFromCloseRelative"
								infoTooltip="Közeli hozzátartozónak számít a szülő, nagyszülő, gyermek (akár örökbefogadott), unoka, testvér és a házastárs is."
								disabled={disabled}
								required
							/>
							<FormDecidable
								label="Az adás-vételi szerződést több, mint 180 napja kötötted?"
								name="earlierBoughtRealEstate"
								disabled={disabled}
								required
							/>
						</>
					)}
					<FormSelect
						label={realEstateBuyingsLabel}
						name="realEstateBuying"
						placeholder="Kérjük, válassz!"
						prepend={<FontAwesomeIcon icon={fasSuitcaseMedical} />}
						options={realEstateBuyings}
						disabled={disabled}
						required
					/>
				</Card.Body>
				<Card.Footer className="text-center">
					<ProgressButton
						type="submit"
						icon={<FontAwesomeIcon icon={fasArrowRight} />}
						className={!form.getState().valid ? "disabled" : undefined}
						state={submitState}
					>
						Tovább
					</ProgressButton>
				</Card.Footer>
			</>
		);
	}

	@autobind
	private validRealEstateValue(realEstateValue: number): string {
		const { MIN_REAL_ESTATE_VALUE, MAX_REAL_ESTATE_VALUE } = CONFIG;

		if (realEstateValue < MIN_REAL_ESTATE_VALUE) {
			return `A vásárolni kívánt ingatlan minimális értéke ${formatText(MIN_REAL_ESTATE_VALUE, "ft")}!`;
		}

		if (realEstateValue > MAX_REAL_ESTATE_VALUE) {
			return `A vásárolni kívánt ingatlan maximális értéke ${formatText(MAX_REAL_ESTATE_VALUE, "ft")}!`;
		}
	}

	protected getComponentName(): string {
		return "Step2";
	}
}
